import { useState } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { TagFacet } from '../documents.types'

type TagFacetSelectorProps = {
  tags: TagFacet[]
  selected: string[]
  onChange: (tags: string[]) => void
  isLoading?: boolean
}

export const TagFacetSelector = ({
  tags,
  selected,
  onChange,
  isLoading,
}: TagFacetSelectorProps) => {
  const [open, setOpen] = useState(false)

  const toggleTag = (tag: string) => {
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag])
  }

  const label =
    selected.length === 0
      ? 'All tags'
      : selected.length === 1
        ? selected[0]
        : `Tags (${selected.length})`

  // Highest count first — the facet has no inherent order from the API, and "most common tag
  // first" is the more useful default than alphabetical for a filter like this.
  const sortedTags = [...tags].sort((a, b) => b.count - a.count)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          // role="combobox" doesn't support name-from-content per the ARIA spec, so the visible
          // label span alone isn't a valid accessible name — reflect it here instead.
          aria-label={selected.length === 0 ? 'Filter by tags' : `Filter by tags: ${label}`}
          disabled={isLoading}
          className="h-10 w-[140px] justify-between rounded-lg bg-background text-sm font-normal text-foreground shadow-sm border border-border hover:text-foreground"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search tags..."
            className="h-8 py-1.5 border-0 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          />
          <CommandList>
            <CommandEmpty>No tags found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__select-all"
                onSelect={() => onChange(sortedTags.map(({ tag }) => tag))}
              >
                Select all
              </CommandItem>
              <CommandItem value="__clear-all" onSelect={() => onChange([])}>
                Clear all
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              {/* Popover stays open across selections — this is a multi-select, not a combobox
                  that picks one value and closes (see toggleTag; no setOpen(false) on select). */}
              {sortedTags.map(({ tag, count }) => (
                <CommandItem key={tag} value={tag} onSelect={() => toggleTag(tag)}>
                  <Checkbox
                    checked={selected.includes(tag)}
                    aria-label={tag}
                    className="pointer-events-none border border-border bg-background text-primary data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary"
                  />
                  <span className="flex-1 truncate">{tag}</span>
                  <span className="text-xs text-muted-foreground">{count}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
